import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { IoSendOutline, IoLinkOutline } from "react-icons/io5";

import api from "../../../../services/api";
import MultiAction from "../../../../components/MultiAction";
import { copyToClipboard } from "../../../../utils";
import { hasAccess } from "../../../../utils/roles";
import { appURL } from "../../../../config";

export default ({ values, setValues, currentUser, projectId }) => {
  const [showSendSigningLinks, setShowSendSigningLinks] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendLinkLoading, setSendLinkLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const isAdmin = hasAccess({ user: currentUser, access: "admin" });

  useEffect(() => {
    if (!projectId) return;
    const fetchUsers = async () => {
      try {
        const { data, ok } = await api.post(`/user/search`, { project_ids: [projectId], status: "active" });
        if (!ok) return;
        setUsers(data.users || []);
      } catch (error) {
        console.error(error);
      }
    };
    fetchUsers();
  }, [projectId]);

  const handleSendSigningLinks = async () => {
    try {
      setSendLinkLoading(true);
      if (selectedRecipients.length === 0) return toast.error("Please select at least one recipient");
      const { ok } = await api.post(`/budget/${values._id}/send-signing-links`, { recipients: selectedRecipients });
      if (!ok) throw new Error("Failed to send signing links");
      toast.success("Signing links sent successfully");
      setShowSendSigningLinks(false);
      setSelectedRecipients([]);
    } catch (error) {
      console.error(error);
      toast.error("Failed to send signing links");
    } finally {
      setSendLinkLoading(false);
    }
  };

  const actions = [];
  if (!values.signed_at || isAdmin) {
    actions.push({ name: "View budget", icon: <IoSendOutline />, cb: () => window.open(`/budget-signature/${values._id}`, "_blank") });
  }
  if (isAdmin) {
    actions.push({
      name: "Copy client link",
      icon: <IoLinkOutline />,
      cb: () => {
        copyToClipboard(`${appURL}/client-view/${values._id}`);
        toast.success("Link copied!");
      },
    });
  }
  if (isAdmin) {
    actions.push({ name: "View Public Estimate", icon: <IoSendOutline />, cb: () => window.open(`/budget-description/${values._id}`, "_blank") });
  }
  if (isAdmin && !values.signed_at) {
    actions.push({ name: "Send signing links", icon: <IoSendOutline />, cb: () => setShowSendSigningLinks(true) });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <MultiAction actions={actions} />
      </div>

      <div className="max-w-xs space-y-2">
        <label className="block text-sm font-medium text-gray-700" htmlFor="budget-signature">
          Signature
        </label>
        <select
          id="budget-signature"
          className="input w-full"
          value={values.signed_at ? "signed" : "unsigned"}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "signed" && !values.signed_at) {
              if (!window.confirm("Are you sure you want to mark this budget as signed ?")) return;
              setValues({
                ...values,
                signed_at: new Date().toISOString(),
                signed_by_id: currentUser?._id,
                signed_by_name: currentUser?.name,
                signed_by_email: currentUser?.email,
                signed_by_avatar: currentUser?.avatar,
              });
            } else if (next === "unsigned" && values.signed_at) {
              if (!window.confirm("Are you sure you want to mark this budget as unsigned ?")) return;
              setValues({
                ...values,
                signed_at: null,
                signed_by_id: null,
                signed_by_name: null,
                signed_by_email: null,
                signed_by_avatar: null,
                signed_by_token: null,
                signed_by_ip: null,
                signed_by_ip_raw: null,
              });
            }
          }}>
          <option value="unsigned">Unsigned</option>
          <option value="signed">Signed</option>
        </select>
      </div>

      {values.signed_at && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>✓</span>
          <span>
            Signed by {values?.signed_by_name || "Unknown User"} on {new Date(values.signed_at).toLocaleDateString()}
          </span>
        </div>
      )}

      <div className="max-w-xs space-y-2">
        <label className="block text-sm font-medium text-gray-700" htmlFor="budget-payment-terms">
          Payment terms (days)
        </label>
        <input
          id="budget-payment-terms"
          className="input w-full"
          type="number"
          min="0"
          value={values.payment_terms_days ?? 30}
          onChange={(e) => setValues({ ...values, payment_terms_days: e.target.value === "" ? 30 : Number(e.target.value) })}
        />
        <p className="text-xs text-gray-500">The invoice is due this many days after it is sent.</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700" htmlFor="budget-contract-url">
          Signed contract
        </label>
        <input
          id="budget-contract-url"
          className="input w-full"
          type="url"
          placeholder="https://app.pandadoc.com/..."
          value={values.contract_url || ""}
          onChange={(e) => setValues({ ...values, contract_url: e.target.value })}
        />
        <p className="text-xs text-gray-500">The PandaDoc link is the proof the client signed.</p>
      </div>

      <div className="max-w-xs space-y-2">
        <label className="block text-sm font-medium text-gray-700" htmlFor="budget-client-signed-at">
          Client signed on
        </label>
        <input
          id="budget-client-signed-at"
          className="input w-full"
          type="date"
          value={values.client_signed_at ? String(values.client_signed_at).split("T")[0] : ""}
          onChange={(e) => setValues({ ...values, client_signed_at: e.target.value || null })}
        />
      </div>

      <div className="max-w-xs space-y-2">
        <label className="block text-sm font-medium text-gray-700" htmlFor="budget-reminder">
          Activation of sign request reminder
        </label>
        <select
          id="budget-reminder"
          className="input w-full"
          value={values.sign_request_reminder_status}
          onChange={(e) => setValues({ ...values, sign_request_reminder_status: e.target.value })}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700" htmlFor="budget-legal">
          Legal Text
        </label>
        <textarea
          id="budget-legal"
          className="input w-full h-24"
          value={values.legal || ""}
          onChange={(e) => setValues({ ...values, legal: e.target.value })}
          placeholder="Enter legal terms and conditions..."
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700" htmlFor="budget-need">
          Need
        </label>
        <textarea
          id="budget-need"
          className="input w-full h-24"
          value={values.need}
          onChange={(e) => setValues({ ...values, need: e.target.value })}
          placeholder="Enter budget need..."
        />
      </div>

      {/* Signing links modal */}
      {showSendSigningLinks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Send signing links</h3>
            <div className="space-y-4 mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Select recipients from the team</label>
                <div className="max-h-48 overflow-y-auto border rounded-md divide-y">
                  {users?.map((teamUser) => (
                    <div key={teamUser._id} className="flex items-center p-2 hover:bg-gray-50">
                      <label className="flex items-center space-x-3 w-full cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedRecipients.some((recipient) => recipient.email === teamUser.email)}
                          onChange={(e) => {
                            setSelectedRecipients(
                              e.target.checked
                                ? [...selectedRecipients, { email: teamUser.email, name: teamUser.name }]
                                : selectedRecipients.filter((recipient) => recipient.email !== teamUser.email),
                            );
                          }}
                          className="rounded border-gray-300"
                        />
                        <div>
                          <div className="text-sm font-medium">{teamUser.name}</div>
                          <div className="text-xs text-gray-500">{teamUser.email}</div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
                <a className="btn gray-btn" href={`/project/${projectId}/team`}>
                  add a client
                </a>
              </div>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowSendSigningLinks(false);
                  setSelectedRecipients([]);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button
                disabled={sendLinkLoading}
                onClick={handleSendSigningLinks}
                className="bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50">
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
